import { useRef, useEffect } from 'react';

// Constants to avoid magic numbers
const DEFAULT_HIGHLIGHT_DURATION = 2000;
const SCROLL_OFFSET = 200;
const SCROLL_DELAY = 400;
const HIGHLIGHT_DELAY = 500;
const HIGHLIGHT_CLASS = 'animate-highlight';

/**
 * Hook for scrolling to and highlighting tickets after CRUD operations.
 *
 * Uses direct DOM manipulation to avoid prop drilling. Requires elements
 * to have `data-ticket-id` attributes and `animate-highlight` CSS class.
 *
 * @returns {Object} { scrollToRecentlyModifiedProduct }
 */
export const useTicketHighlight = () => {
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentHighlightedElement = useRef<Element | null>(null);

  useEffect(
    () => () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (currentHighlightedElement.current) {
        currentHighlightedElement.current.classList.remove(HIGHLIGHT_CLASS);
      }
    },
    []
  );

  const clearHighlight = () => {
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
    if (currentHighlightedElement.current) {
      currentHighlightedElement.current.classList.remove(HIGHLIGHT_CLASS);
      currentHighlightedElement.current = null;
    }
  };

  const highlightTicket = (
    ticketId: string,
    duration: number = DEFAULT_HIGHLIGHT_DURATION
  ) => {
    clearHighlight();

    const ticketElement = document.querySelector(
      `[data-ticket-id="${ticketId}"]`
    );
    if (!ticketElement) {
      return;
    }

    ticketElement.classList.add(HIGHLIGHT_CLASS);
    currentHighlightedElement.current = ticketElement;

    highlightTimeoutRef.current = setTimeout(() => {
      ticketElement.classList.remove(HIGHLIGHT_CLASS);
      currentHighlightedElement.current = null;
      highlightTimeoutRef.current = null;
    }, duration);
  };

  const highlightTickets = (ticketIds: string[]) => {
    clearHighlight();

    const ticketElements = ticketIds
      .map((ticketId) =>
        document.querySelector(`[data-ticket-id="${ticketId}"]`)
      )
      .filter(Boolean);

    if (ticketElements.length === 0) {
      return;
    }

    // Add highlight class to all ticket elements
    ticketElements.forEach((element) => {
      element.classList.add(HIGHLIGHT_CLASS);
    });

    // Set up timeout to remove highlights
    highlightTimeoutRef.current = setTimeout(() => {
      ticketElements.forEach((element) => {
        element.classList.remove(HIGHLIGHT_CLASS);
      });
      currentHighlightedElement.current = null;
      highlightTimeoutRef.current = null;
    }, DEFAULT_HIGHLIGHT_DURATION);

    // Store reference to the first element for cleanup purposes
    currentHighlightedElement.current = ticketElements[0];
  };

  const scrollToTicket = (ticketId: string) => {
    const ticketElement = document.querySelector(
      `[data-ticket-id="${ticketId}"]`
    );

    if (ticketElement) {
      window.scrollTo({
        top:
          ticketElement.getBoundingClientRect().top +
          window.pageYOffset -
          SCROLL_OFFSET,
        behavior: 'smooth'
      });
      return true;
    }
    return false;
  };

  const scrollToRecentlyModifiedProduct = (productId: string) => {
    if (!productId) {
      return;
    }

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const scrolled = scrollToTicket(productId);

      if (scrolled) {
        setTimeout(() => {
          highlightTicket(productId);
        }, HIGHLIGHT_DELAY);
      }

      scrollTimeoutRef.current = null;
    }, SCROLL_DELAY);
  };

  return {
    scrollToRecentlyModifiedProduct,
    highlightTickets
  };
};
